import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactSVG } from "react-svg";

import debounce from "lodash.debounce";
import { XMarkIcon } from "@heroicons/react/24/solid";

import { setPatientsQueryParams } from "../../redux/modules/patient/actions";
import { convertToPascalCase } from '../../helpers/functions';

const STRICT_ISO_DOB = /^\d{4}-\d{2}-\d{2}$/;

const Search = ({ disabled, onSearchCleared }) => {
  const [searchValue, setSearchValue] = useState("");
  const dispatch = useDispatch();
  const { queryParams = {} } = useSelector((state) => state.patients);

  const debouncedSetQueryParamsRef = useRef(
    debounce((params) => {
      dispatch(setPatientsQueryParams(params));
    }, 300),
  );

  const buildQueryParams = (rawInput) => {
    const s = String(rawInput || "").trim();
    if (!s) {
      return {
        page: 1,
        search: "",
        advancedDob: "",
        advancedPhone: "",
        advancedHealthCard: "",
        advancedVersionCode: "",
      };
    }

    if (/^\d+$/.test(s)) {
      return {
        page: 1,
        search: "",
        advancedPhone: s,
        advancedHealthCard: s,
        advancedDob: "",
        advancedVersionCode: "",
      };
    }

    if (STRICT_ISO_DOB.test(s)) {
      return {
        page: 1,
        search: "",
        advancedDob: s,
        advancedPhone: "",
        advancedHealthCard: "",
        advancedVersionCode: "",
      };
    }

    return {
      page: 1,
      search: convertToPascalCase(s),
      advancedDob: "",
      advancedPhone: "",
      advancedHealthCard: "",
      advancedVersionCode: "",
    };
  };

  const handleSearchChange = (newSearch) => {
    const capitalizedValue = convertToPascalCase(newSearch);
    setSearchValue(capitalizedValue);

    if (!String(newSearch || "").trim()) {
      debouncedSetQueryParamsRef.current.cancel();
      if (typeof onSearchCleared === "function") {
        onSearchCleared();
      } else {
        dispatch(setPatientsQueryParams(buildQueryParams("")));
      }
      return;
    }

    const nextParams = buildQueryParams(newSearch);
    debouncedSetQueryParamsRef.current(nextParams);
  };

  const handleClear = () => {
    setSearchValue("");
    debouncedSetQueryParamsRef.current.cancel();
    if (typeof onSearchCleared === "function") {
      onSearchCleared();
    } else {
      dispatch(setPatientsQueryParams(buildQueryParams("")));
    }
  };

  useEffect(() => {
    const currentSearchValue = String(
      queryParams?.search ||
        queryParams?.advancedPhone ||
        queryParams?.advancedHealthCard ||
        queryParams?.advancedDob ||
        "",
    );
    if (currentSearchValue !== searchValue) {
      setSearchValue(currentSearchValue);
    }
  }, [queryParams?.search, queryParams?.advancedPhone, queryParams?.advancedHealthCard, queryParams?.advancedDob]);

  useEffect(() => {
    return () => {
      debouncedSetQueryParamsRef.current.cancel();
    };
  }, []);

  return (
    <div
      className={`relative flex w-full h-[80px] flex-col ${
        disabled ? "pointer-events-none  cursor-not-allowed opacity-50" : ""
      }  `}
    >
      <div className="flex rounded-md shadow-sm h-[80px]">
        <div className="relative flex flex-grow">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 ">
            <ReactSVG
              src="/icons/search.svg"
              className="w-4 h-4 cursor-pointer "
            />
          </div>
          <input
            type="text"
            name="patientSearch"
            id="patientSearch"
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="block w-full text-gray-900 placeholder:text-gray-400 border-gray-100 border rounded-[4px] py-3.5 focus:ring-0 focus:outline-none pl-10"
            placeholder="Health Card, Patient Name, DOB (YYYYMMDD) or Phone"
          />
          {!!searchValue && (
            <XMarkIcon
              className="absolute inset-y-6 right-3 w-6 h-6 cursor-pointer text-teal-300 z-10"
              onClick={handleClear}
            />
          )}
        </div>
      </div>

    </div>
  );
};

export default Search;
